import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import Footer from '../../components/Footer';
import logo from '../../assets/logo.png';
import { getDeliveryLinks, trackDeliveryClick } from '../../utils/deliveryAffiliates';
import { getNudgeSettings, saveNudgeSettings, requestNotificationPermission, getNotificationPermission } from '../../utils/hungerNudge';
import './ResultsScreen.css';

const NUDGE_PROMPT_DISMISSED_KEY = 'hangrily_nudge_prompt_dismissed';

// Shown once after a completed session — this is the single highest-intent
// moment to ask permission to remind someone next time they're hungry,
// since they just felt the value of the app solve their "what to eat" problem.
function NudgeOptInCard() {
  const [visible, setVisible] = useState(() => {
    if (localStorage.getItem(NUDGE_PROMPT_DISMISSED_KEY)) return false;
    if (getNudgeSettings().enabled) return false;
    return getNotificationPermission() === 'default';
  });
  const [status, setStatus] = useState('idle'); // idle | granted | denied

  if (!visible) return null;

  async function handleEnable() {
    const perm = await requestNotificationPermission();
    if (perm === 'granted') {
      saveNudgeSettings({ ...getNudgeSettings(), enabled: true });
      setStatus('granted');
    } else {
      setStatus('denied');
    }
    localStorage.setItem(NUDGE_PROMPT_DISMISSED_KEY, '1');
    setTimeout(() => setVisible(false), 2000);
  }

  function handleDismiss() {
    localStorage.setItem(NUDGE_PROMPT_DISMISSED_KEY, '1');
    setVisible(false);
  }

  return (
    <div style={{
      background: '#1a1a1a',
      border: '1px solid #333',
      borderRadius: '12px',
      padding: '16px',
      marginTop: '16px',
      textAlign: 'center',
    }}>
      {status === 'idle' && (
        <>
          <div style={{ fontSize: '22px', marginBottom: '6px' }}>🔔</div>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#ccc' }}>
            Never wonder what's for dinner again — get a quick nudge at lunch and dinner time.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={handleDismiss}
              style={{ background: 'transparent', color: '#888', border: '1px solid #444', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}
            >
              Not now
            </button>
            <button
              onClick={handleEnable}
              style={{ background: '#FF6347', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
            >
              Remind Me
            </button>
          </div>
        </>
      )}
      {status === 'granted' && <p style={{ margin: 0, fontSize: '13px', color: '#4caf50' }}>✓ We'll remind you at mealtime!</p>}
      {status === 'denied' && <p style={{ margin: 0, fontSize: '13px', color: '#888' }}>No problem — you can enable reminders later from Home.</p>}
    </div>
  );
}

function CashbackBanner({ sessionId, restaurant, navigate }) {
  const { user } = useAuth();
  
  if (!user) return null;
  
  const isPremium = user.is_premium || ['active', 'trialing'].includes(user.subscription_status);
  
  if (!isPremium) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        padding: '16px',
        borderRadius: '12px',
        marginTop: '16px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>💎</div>
        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Upgrade to Premium</h4>
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', opacity: 0.9 }}>
          Earn $1 cashback on every verified order ($4.99/mo)
        </p>
        <button
          onClick={() => navigate('/subscription')}
          style={{
            background: '#fff',
            color: '#667eea',
            border: 'none',
            padding: '8px 20px',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Learn More
        </button>
      </div>
    );
  }
  
  return (
    <div style={{
      background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
      color: '#fff',
      padding: '16px',
      borderRadius: '12px',
      marginTop: '16px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '32px', marginBottom: '8px' }}>💰</div>
      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Earn $1 Cashback</h4>
      <p style={{ margin: '0 0 12px 0', fontSize: '13px', opacity: 0.95 }}>
        Upload a receipt for orders $10+ to earn cashback (up to 10/mo)!
      </p>
      <button
        onClick={() => navigate('/visit-confirmation', { 
          state: { sessionId, restaurant } 
        })}
        style={{
          background: '#fff',
          color: '#4caf50',
          border: 'none',
          padding: '10px 24px',
          borderRadius: '8px',
          fontWeight: '600',
          cursor: 'pointer',
          fontSize: '14px',
        }}
      >
        Confirm Visit & Earn
      </button>
    </div>
  );
}

export default function ResultsScreen() {
  const navigate = useNavigate();
  const { code } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favoritedIds, setFavoritedIds] = useState(new Set());
  const [tiedPicks, setTiedPicks] = useState([]);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [showTieBreaker, setShowTieBreaker] = useState(false);
  const [decisionSaved, setDecisionSaved] = useState(false);
  const [showRunnerUps, setShowRunnerUps] = useState(false);
  const [swipeInsights, setSwipeInsights] = useState(null);
  const [showInsights, setShowInsights] = useState(false);

  const loadResults = useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/${code}/final`);
      setResults(data);
      
      // Check for ties at the top score
      if (data.finalPicks && data.finalPicks.length > 1) {
        const topScore = data.finalPicks[0].totalScore;
        const tied = data.finalPicks.filter(p => p.totalScore === topScore);
        
        if (tied.length > 1) {
          setTiedPicks(tied);
          setShowTieBreaker(true);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load results:', error);
      alert('Failed to load results');
      navigate('/');
    }
  }, [code, navigate]);

  useEffect(() => {
    loadResults();
    // Load swipe insights from sessionStorage
    const storedInsights = sessionStorage.getItem('lastSwipeStats');
    if (storedInsights) {
      try {
        setSwipeInsights(JSON.parse(storedInsights));
        sessionStorage.removeItem('lastSwipeStats');
      } catch (err) {
        console.error('Failed to parse swipe insights:', err);
      }
    }
  }, [loadResults]);

  // Save decision to backend (stats, points, history tracking)
  // Only save after tie-breaker is resolved (or if there's no tie)
  useEffect(() => {
    if (!results || !code || decisionSaved || showTieBreaker) return;
    const winnerPick = selectedWinner || results.finalPicks?.[0];
    if (!winnerPick?.restaurant) return;
    const saveDecision = async () => {
      try {
        await api.post('/decisions/save', {
          sessionCode: code,
          mode: results.participants?.length > 1 ? (results.participants.length === 2 ? 'couple' : 'group') : 'solo',
          finalRestaurant: winnerPick.restaurant,
          rounds: results.roundResults || [],
          totalTime: results.duration || 0,
        });
        setDecisionSaved(true);
      } catch { /* non-critical */ }
    };
    saveDecision();
  }, [results, selectedWinner, showTieBreaker]); // eslint-disable-line react-hooks/exhaustive-deps

  function openInMaps(restaurant) {
    const query = encodeURIComponent(`${restaurant.name} ${restaurant.address || ''}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  }

  async function toggleFavorite(restaurant) {
    const isFav = favoritedIds.has(restaurant.id);
    if (isFav) {
      setFavoritedIds(prev => { const next = new Set(prev); next.delete(restaurant.id); return next; });
      try { await api.delete(`/favorites/${restaurant.id}`); } catch { setFavoritedIds(prev => new Set([...prev, restaurant.id])); }
    } else {
      setFavoritedIds(prev => new Set([...prev, restaurant.id]));
      try { await api.post('/favorites', { restaurant }); } catch { setFavoritedIds(prev => { const next = new Set(prev); next.delete(restaurant.id); return next; }); }
    }
  }

  function handleDeliveryClick(service, restaurantName, url) {
    trackDeliveryClick(service, restaurantName);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function handleTieBreakerChoice(pick) {
    setSelectedWinner(pick);
    setShowTieBreaker(false);
  }

  function handleTryAgain() {
    // Re-swipe through runner-ups (top 3-5 excluding winner)
    const winner = selectedWinner?.restaurant || results.finalPicks[0]?.restaurant;
    const runnerUps = results.finalPicks.slice(1, 4).filter(p => p.restaurant.id !== winner.id);
    
    if (runnerUps.length > 0) {
      setShowRunnerUps(true);
    } else {
      // Start fresh session with same preferences
      navigate('/preferences', { state: { mode: 'solo' } });
    }
  }

  function selectRunnerUp(pick) {
    setSelectedWinner(pick);
    setShowRunnerUps(false);
  }

  function shareResults() {
    const winner = selectedWinner?.restaurant || results.finalPicks[0]?.restaurant;
    const text = `We're eating at ${winner.name}! 🍕 Found via Hangrily`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Hangrily Decision',
        text: text,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(text);
      alert('Results copied to clipboard!');
    }
  }

  if (loading) {
    return (
      <div className="results-screen">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  // Safety check: no final picks at all
  if (!results?.finalPicks || results.finalPicks.length === 0) {
    return (
      <div className="results-screen">
        <header className="results-header">
          <img src={logo} alt="Hangrily" onClick={() => navigate('/')} style={{ height: '28px', cursor: 'pointer' }} />
          <h1 className="results-title">No Clear Winner</h1>
        </header>
        <main className="results-main">
          <div className="results-container">
            <div className="confetti-animation">🤷</div>
            <h2 style={{ marginBottom: '12px' }}>Couldn't pick a winner this time!</h2>
            <p style={{ color: '#888', marginBottom: '24px' }}>Try again with different preferences or fewer filters.</p>
            <button className="try-again-btn" onClick={() => navigate('/preferences', { state: { mode: 'solo' } })}>
              ✨ Start Fresh Session
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show tie-breaker screen if there's a tie
  if (showTieBreaker && tiedPicks.length > 1) {
    return (
      <div className="results-screen">
        <header className="results-header">
          <img src={logo} alt="Hangrily" onClick={() => navigate('/')} style={{ height: '28px', cursor: 'pointer' }} />
          <h1 className="results-title">It's a Tie!</h1>
        </header>

        <main className="results-main">
          <div className="results-container">
            <div className="confetti-animation">🤝</div>
            <h2 className="tiebreaker-title">These restaurants tied with {tiedPicks[0].totalScore} points!</h2>
            <p className="tiebreaker-subtitle">Pick your winner:</p>
            
            <div className="tiebreaker-grid">
              {tiedPicks.map((pick, idx) => (
                <div 
                  key={pick.restaurant.id}
                  className="tiebreaker-card"
                  onClick={() => handleTieBreakerChoice(pick)}
                >
                  {pick.restaurant.image && (
                    <div className="tiebreaker-image" style={{ backgroundImage: `url(${pick.restaurant.image})` }} />
                  )}
                  <div className="tiebreaker-content">
                    <h3 className="tiebreaker-name">{pick.restaurant.name}</h3>
                    <p className="tiebreaker-cuisine">{pick.restaurant.cuisine}</p>
                    <div className="tiebreaker-meta">
                      <span>{'$'.repeat(pick.restaurant.priceLevel || 2)}</span>
                      {pick.restaurant.rating > 0 && <span>⭐ {pick.restaurant.rating.toFixed(1)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const winner = selectedWinner || results.finalPicks[0];
  const runnerUps = results.finalPicks.slice(1).filter(p => p.restaurant.id !== winner.restaurant.id);
  const duration = results.duration ? `${Math.floor(results.duration / 60)}m ${results.duration % 60}s` : '';
  const deliveryLinks = getDeliveryLinks(winner.restaurant);

  return (
    <div className="results-screen">
      <header className="results-header">
        <img src={logo} alt="Hangrily" onClick={() => navigate('/')} style={{ height: '28px', cursor: 'pointer' }} />
        <h1 className="results-title">Decision Made!</h1>
        <button onClick={shareResults} className="share-btn">📤 Share</button>
      </header>

      <main className="results-main">
        <div className="results-container">
          <div className="confetti-animation">🎉</div>
          
          <div className="winner-section">
            <h2 className="winner-label">You're eating at...</h2>
            
            <div className="winner-card">
              {winner.restaurant.image && (
                <div 
                  className="winner-image" 
                  style={{ backgroundImage: `url(${winner.restaurant.image})` }}
                >
                  <div className="winner-badge">🏆 Winner</div>
                </div>
              )}
              
              <div className="winner-content">
                <h3 className="winner-name">{winner.restaurant.name}</h3>
                
                <div className="winner-meta">
                  <span className="meta-item">
                    🍽️ {winner.restaurant.cuisine}
                  </span>
                  <span className="meta-item">
                    💰 {'$'.repeat(winner.restaurant.priceLevel || 2)}
                  </span>
                  {winner.restaurant.rating > 0 && (
                    <span className="meta-item">
                      ⭐ {winner.restaurant.rating.toFixed(1)}
                    </span>
                  )}
                </div>

                {winner.restaurant.address && (
                  <p className="winner-address">📍 {winner.restaurant.address}</p>
                )}

                {winner.restaurant.distance && (
                  <p className="winner-distance">🚗 {winner.restaurant.distance}</p>
                )}

                <div className="winner-score">
                  <span className="score-label">Match Score:</span>
                  <span className="score-value">{winner.totalScore} points</span>
                </div>

                <div className="winner-actions">
                  <button 
                    className="results-action-btn primary-action"
                    onClick={() => openInMaps(winner.restaurant)}
                  >
                    🗺️ Get Directions
                  </button>
                  <button
                    className={`results-action-btn fav-action ${favoritedIds.has(winner.restaurant.id) ? 'favorited' : ''}`}
                    onClick={() => toggleFavorite(winner.restaurant)}
                  >
                    {favoritedIds.has(winner.restaurant.id) ? '❤️ Saved' : '🤍 Favorite'}
                  </button>
                </div>

                <div className="ordering-section">
                  <h4 className="ordering-title">🛵 Order or Reserve</h4>
                  <div className="ordering-buttons">
                    {deliveryLinks.map((svc) => (
                      <button
                        key={svc.name}
                        className="ordering-btn"
                        style={{ background: svc.color }}
                        onClick={() => handleDeliveryClick(svc.name, winner.restaurant.name, svc.url)}
                      >
                        <span className="ordering-btn-name">{svc.name}</span>
                      </button>
                    ))}
                  </div>
                  <p className="ordering-note">
                    Opens partner site — availability varies by location.
                  </p>
                </div>

                {/* Cashback Banner for Premium Users */}
                <CashbackBanner 
                  sessionId={results?.sessionId} 
                  restaurant={winner.restaurant}
                  navigate={navigate}
                />

                {/* Meal-time reminder opt-in — highest-intent moment to ask */}
                <NudgeOptInCard />

                {/* Try Again Button */}
                <div className="try-again-section">
                  <button 
                    className="try-again-btn"
                    onClick={handleTryAgain}
                  >
                    🔄 Not feeling it? Try runner-ups
                  </button>
                  <button 
                    className="start-fresh-btn"
                    onClick={() => navigate('/preferences', { state: { mode: 'solo' } })}
                  >
                    ✨ Start fresh session
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Swipe Insights Panel */}
          {swipeInsights && (
            <div className="insights-section">
              <div className="insights-header" onClick={() => setShowInsights(!showInsights)}>
                <h3>📊 Your Swipe Summary</h3>
                <span>{showInsights ? '▼' : '▶'}</span>
              </div>
              {showInsights && (
                <div className="insights-content">
                  <div className="insight-stat">
                    <span className="stat-emoji">❤️</span>
                    <span className="stat-label">Loved:</span>
                    <span className="stat-value">{swipeInsights.loved.length} restaurants</span>
                  </div>
                  <div className="insight-stat">
                    <span className="stat-emoji">👍</span>
                    <span className="stat-label">Liked:</span>
                    <span className="stat-value">{swipeInsights.liked.length} restaurants</span>
                  </div>
                  <div className="insight-stat">
                    <span className="stat-emoji">👎</span>
                    <span className="stat-label">Disliked:</span>
                    <span className="stat-value">{swipeInsights.disliked.length} restaurants</span>
                  </div>
                  {swipeInsights.loved.length > 0 && (
                    <div className="insight-detail">
                      <p className="insight-text">
                        💡 You loved {swipeInsights.loved.every(r => r.cuisine === swipeInsights.loved[0].cuisine) 
                          ? `${swipeInsights.loved[0].cuisine} spots!` 
                          : 'a variety of cuisines!'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Runner-Ups Modal */}
          {showRunnerUps && (
            <>
              <div className="modal-overlay" onClick={() => setShowRunnerUps(false)} />
              <div className="modal-content runner-ups-modal">
                <h2 style={{ marginBottom: '16px', fontSize: '24px', fontWeight: '700' }}>
                  🔄 Try These Instead
                </h2>
                <p style={{ marginBottom: '20px', color: '#888', fontSize: '14px' }}>
                  Pick from your top runner-ups
                </p>
                <div className="runner-ups-list">
                  {results.finalPicks.slice(1, 4).map((pick) => (
                    <div 
                      key={pick.restaurant.id} 
                      className="runner-up-option"
                      onClick={() => selectRunnerUp(pick)}
                    >
                      <div className="runner-up-info">
                        <h3>{pick.restaurant.name}</h3>
                        <p>{pick.restaurant.cuisine} • {'$'.repeat(pick.restaurant.priceLevel || 2)}</p>
                        {pick.restaurant.rating > 0 && (
                          <span className="runner-rating">⭐ {pick.restaurant.rating.toFixed(1)}</span>
                        )}
                      </div>
                      <div className="runner-score">{pick.totalScore} pts</div>
                    </div>
                  ))}
                </div>
                <button 
                  className="btn btn-secondary"
                  style={{ marginTop: '16px', width: '100%' }}
                  onClick={() => setShowRunnerUps(false)}
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          {runnerUps.length > 0 && (
            <div className="runners-up-section">
              <h3 className="section-title">Runner-Ups</h3>
              <div className="runners-grid">
                {runnerUps.map((pick, index) => (
                  <div key={pick.restaurant.id} className="runner-card">
                    <div className="runner-rank">#{pick.rank}</div>
                    <h4 className="runner-name">{pick.restaurant.name}</h4>
                    <p className="runner-cuisine">{pick.restaurant.cuisine}</p>
                    <div className="runner-meta">
                      <span>{'$'.repeat(pick.restaurant.priceLevel || 2)}</span>
                      {pick.restaurant.rating > 0 && (
                        <span>⭐ {pick.restaurant.rating.toFixed(1)}</span>
                      )}
                    </div>
                    <p className="runner-score">{pick.totalScore} pts</p>
                    <div className="runner-actions">
                      <button 
                        className="runner-btn"
                        onClick={() => openInMaps(pick.restaurant)}
                      >
                        📍
                      </button>
                      <button
                        className={`runner-btn fav-btn ${favoritedIds.has(pick.restaurant.id) ? 'favorited' : ''}`}
                        onClick={() => toggleFavorite(pick.restaurant)}
                      >
                        {favoritedIds.has(pick.restaurant.id) ? '❤️' : '🤍'}
                      </button>
                    </div>
                    <div className="runner-delivery">
                      {getDeliveryLinks(pick.restaurant).map((svc) => (
                        <button
                          key={svc.name}
                          className="runner-delivery-btn"
                          style={{ background: svc.color }}
                          onClick={() => handleDeliveryClick(svc.name, pick.restaurant.name, svc.url)}
                        >
                          {svc.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {results.bothLoved && results.bothLoved.length > 0 && (
            <div className="both-loved-section">
              <div className="both-loved-header">
                <span className="both-loved-emoji">💕</span>
                <h3 className="both-loved-title">You Both Loved</h3>
                <p className="both-loved-subtitle">Restaurants you and your partner both swiped right on</p>
              </div>
              <div className="both-loved-grid">
                {results.bothLoved.map(({ restaurant, bothLovedIt }) => (
                  <div key={restaurant.id} className={`both-loved-card ${bothLovedIt ? 'super-match' : ''}`}>
                    {bothLovedIt && <div className="super-match-badge">🔥 Super Match</div>}
                    <div className="both-loved-info">
                      <h4 className="both-loved-name">{restaurant.name}</h4>
                      <p className="both-loved-cuisine">{restaurant.cuisine}</p>
                      <div className="both-loved-meta">
                        <span>{'$'.repeat(restaurant.priceLevel || 2)}</span>
                        {restaurant.rating > 0 && <span>⭐ {restaurant.rating.toFixed(1)}</span>}
                      </div>
                    </div>
                    <div className="both-loved-actions">
                      <button className="runner-btn" onClick={() => openInMaps(restaurant)}>📍</button>
                      <button
                        className={`runner-btn fav-btn ${favoritedIds.has(restaurant.id) ? 'favorited' : ''}`}
                        onClick={() => toggleFavorite(restaurant)}
                      >
                        {favoritedIds.has(restaurant.id) ? '❤️' : '🤍'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button className="both-loved-share-btn" onClick={() => {
                const names = results.bothLoved.slice(0, 3).map(b => b.restaurant.name).join(', ');
                const text = `We both loved ${names}! 💕 Found our match on Hangrily`;
                if (navigator.share) {
                  navigator.share({ title: 'Our Restaurant Matches', text, url: 'https://hangrily.app' });
                } else {
                  navigator.clipboard.writeText(text);
                  alert('Copied to clipboard!');
                }
              }}>
                📤 Share Our Matches
              </button>
            </div>
          )}

          <div className="session-stats">
            <h3 className="section-title">Session Stats</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-value">{results.totalRounds}</span>
                <span className="stat-label">Rounds</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{results.participants?.length || 1}</span>
                <span className="stat-label">Participants</span>
              </div>
              {duration && (
                <div className="stat-item">
                  <span className="stat-value">{duration}</span>
                  <span className="stat-label">Duration</span>
                </div>
              )}
            </div>
          </div>

          <div className="results-actions">
            <button 
              className="btn btn-primary btn-lg"
              onClick={() => navigate('/')}
            >
              Start New Session
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
